import { Head, Link } from "@inertiajs/react"
import { ChevronRight, Heart } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"

type DonationRow = {
  id: number
  user_id: number
  user_email: string
  user_name: string
  tier: string
  amount_cents: number
  currency: string
  donated_at: string
}

type Props = {
  donations: DonationRow[]
  total_cents: number
  donor_count: number
}

export default function AdminDonationsIndex({ donations, total_cents, donor_count }: Props) {
  return (
    <>
      <Head title="Donations — Admin">
        <meta name="description" content="View all donations in the admin area." />
        <meta property="og:title" content="Donations — Admin" />
        <meta property="og:description" content="View all donations in the admin area." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Donations</h1>
              <p className="mt-1">
                {donations.length} {donations.length === 1 ? "donation" : "donations"} from{" "}
                {donor_count} {donor_count === 1 ? "donor" : "donors"}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold text-ink-display">{formatAUD(total_cents)}</p>
              <p className="text-xs text-ink-muted">total raised</p>
            </div>
          </div>
        </div>

        {donations.length === 0 ? (
          <div className="mt-12 text-center text-ink-muted">
            <Heart className="mx-auto w-8 h-8 mb-3 opacity-30" />
            <p>No donations yet.</p>
          </div>
        ) : (
          <ul className="mt-6 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
            {donations.map((d) => (
              <li key={d.id}>
                <Link
                  href={`/admin/donations/${d.id}`}
                  className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-surface"
                >
                  <Heart className="h-4 w-4 text-ink-muted shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-ink-display">
                      {d.user_name || d.user_email}
                    </div>
                    <div className="truncate text-xs text-ink-muted">
                      {d.user_email} · {formatDate(d.donated_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm font-medium text-ink-body">{formatAUD(d.amount_cents)}</span>
                    <Badge tone={d.tier === "lifetime" ? "accent" : "signal"}>
                      {d.tier === "lifetime" ? "Lifetime" : "Annual"}
                    </Badge>
                  </div>
                  <ChevronRight className="h-4 w-4 text-ink-muted shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </AdminShell>
    </>
  )
}

function formatAUD(cents: number) {
  return `$${(cents / 100).toFixed(2)} AUD`
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
